import Popup from "components/pop-up/pop-up";
import "./fileViewer.css";
import { useEffect, useState } from "react";

interface FileViewerProps {
  selectedApplicantDocument?: {
    name: string;
    size: number;
    url: string;
    isFetched: boolean;
    type: string;
    uuid: string;
  };
  isPopupOpen: boolean;
  setIsPopupOpen: React.Dispatch<React.SetStateAction<boolean>>;
}

export default function FileViewer({
  selectedApplicantDocument,
  setIsPopupOpen,
  isPopupOpen,
}: FileViewerProps) {
  const [viewFile, setViewFile] = useState<string>("");
  const [fileType, setFileType] = useState<string>("");

  useEffect(() => {
    if (isPopupOpen && selectedApplicantDocument) {
      handleView(
        selectedApplicantDocument.uuid,
        selectedApplicantDocument.type
      );
    }
  }, [isPopupOpen, selectedApplicantDocument]);

  // Function to get document URL based on type
  const getFetchUrl = (uuid: string, type: string) => {
    switch (type) {
      case "court_bail":
        return `${process.env.REACT_APP_API_BASE_URL}asset_recovery/suspect-documents/document/${uuid}/`;
      case "police_bail":
        return `${process.env.REACT_APP_API_BASE_URL}asset_recovery/suspect-documents/document/${uuid}/`;
      case "evidence_file":
        return `${process.env.REACT_APP_API_BASE_URL}asset_recovery/asset-evidence-documents/document/${uuid}/`;
      case "letter_file":
        return `${process.env.REACT_APP_API_BASE_URL}asset_recovery/asset-referral-documents/document/${uuid}/`;
      case "investigation_summary_file":
        return `${process.env.REACT_APP_API_BASE_URL}asset_recovery/asset-referral-documents/document/${uuid}/`;
      case "decision_document":
        return `${process.env.REACT_APP_API_BASE_URL}asset_recovery/asset-referral-documents/document/${uuid}/`;
      case "termination_order_document":
        return `${process.env.REACT_APP_API_BASE_URL}asset_recovery/asset-referral-documents/document/${uuid}/`;
      case "prohabition_request_document":
        return `${process.env.REACT_APP_API_BASE_URL}asset_recovery/asset-referral-documents/document/${uuid}/`;
      case "reinitiate_request_document":
        return `${process.env.REACT_APP_API_BASE_URL}asset_recovery/asset-referral-documents/document/${uuid}/`;
      case "suspension_request_document":
        return `${process.env.REACT_APP_API_BASE_URL}asset_recovery/asset-referral-documents/document/${uuid}/`;
      case "asset_recovery_request_document":
        return `${process.env.REACT_APP_API_BASE_URL}asset_recovery/asset-referral-documents/document/${uuid}/`;
      case "court_decision_evidence":
        return `${process.env.REACT_APP_API_BASE_URL}asset_recovery/asset-prohabition-daily-report-final-verdict-documents/document/${uuid}/`;
      case "daily_report_summary":
        return `${process.env.REACT_APP_API_BASE_URL}asset_recovery/asset-prohabition-daily-report-final-verdict-documents/document/${uuid}/`;
      case "feedback_document":
        return `${process.env.REACT_APP_API_BASE_URL}asset_recovery/referral_feedback_documents/document/${uuid}/`;
      case "ownership_proof":
        return `${process.env.REACT_APP_API_BASE_URL}asset_recovery/owner-documents/document/${uuid}/`;
      case "court_decision_evidence_assetrecovery":
        return `${process.env.REACT_APP_API_BASE_URL}asset_recovery/asset-recovery-final-verdict-documents/document/${uuid}/`;
      case "daily_report_summary_assetrecovery":
        return `${process.env.REACT_APP_API_BASE_URL}asset_recovery_appeal/daily-report-documents/${uuid}/`;
      case "daily_report_summary_file":
        return `${process.env.REACT_APP_API_BASE_URL}asset_recovery/asset-recovery-final-verdict-documents/document/${uuid}/`;
      case "appeal_claim_document":
        return `${process.env.REACT_APP_API_BASE_URL}asset_recovery_appeal/appeal-request-documents/document/${uuid}/`;
      case "feedback_file":
        return `${process.env.REACT_APP_API_BASE_URL}asset_recovery_appeal/daily-report-documents/document/${uuid}/`;
      case "court_decision_file":
        return `${process.env.REACT_APP_API_BASE_URL}asset_recovery/asset-recovery-final-verdict-documents/document/${uuid}/`;

      case "prosecutor_response_file":
        return `${process.env.REACT_APP_API_BASE_URL}asset_recovery_appeal/asset-appeal-respondant-documents/document/${uuid}/`;
      case "court_summon_respondant_appeal_file":
        return `${process.env.REACT_APP_API_BASE_URL}asset_recovery_appeal/asset-appeal-respondant-documents/document/${uuid}/`;
      default:
        return `${process.env.REACT_APP_API_BASE_URL}asset_recovery/employee/documents/read/${uuid}/`;
    }
  };
  // Function to view document
  const handleView = async (uuid: string, type: string) => {
    const fetchUrl = getFetchUrl(uuid, type);
    // console.log({ fetchUrl });
    try {
      const response = await fetch(fetchUrl);
      if (!response.ok) throw new Error("Failed to fetch document");

      const blob = await response.blob();
      const contentType = blob.type;

      const fileUrl = URL.createObjectURL(blob);
      setViewFile(fileUrl);
      setFileType(contentType.includes("pdf") ? "pdf" : "image");
    } catch (error) {
      console.log("Error viewing document:", error);
    }
  };

  // Function to Download Document
  const handleDownload = async (uuid: string, type: string) => {
    const fetchUrl = getFetchUrl(uuid, type);

    try {
      const response = await fetch(fetchUrl);
      if (!response.ok) throw new Error("Failed to download document");

      const blob = await response.blob();
      const extension = blob.type.split("/")[1];
      const fileUrl = URL.createObjectURL(blob);

      const link = document.createElement("a");
      link.href = fileUrl;
      link.download = `${selectedApplicantDocument?.name}.${extension}`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(fileUrl);
    } catch (error) {
      console.log("Error downloading document:", error);
    }
  };

  return (
    <Popup isOpen={isPopupOpen}>
      <div className="applicant-container overflow-y-auto file-viewer">
        {viewFile && (
          <div className="viewer-container">
            {fileType === "pdf" ? (
              <iframe
                className="pdf-viewer"
                src={`${viewFile}#toolbar=0&navpanes=0&scrollbar=0`}
              ></iframe>
            ) : (
              <img
                className="image-viewer"
                src={viewFile}
                alt="Document Preview"
              />
            )}
          </div>
        )}

        <div>
          <div className="applicant-detail relative">
            <svg
              width="24"
              height="24"
              viewBox="0 0 24 24"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
              onClick={() => setIsPopupOpen(false)}
              className="cursor-pointer"
            >
              <path
                d="M16.95 8.46404C17.1322 8.27544 17.233 8.02283 17.2307 7.76064C17.2284 7.49844 17.1232 7.24763 16.9378 7.06222C16.7524 6.87681 16.5016 6.77164 16.2394 6.76936C15.9772 6.76709 15.7246 6.86788 15.536 7.05004L12 10.586L8.464 7.05004C8.37175 6.95453 8.26141 6.87835 8.13941 6.82594C8.0174 6.77353 7.88618 6.74594 7.7534 6.74479C7.62062 6.74363 7.48894 6.76894 7.36605 6.81922C7.24315 6.8695 7.1315 6.94375 7.03761 7.03764C6.94371 7.13154 6.86946 7.24319 6.81918 7.36609C6.7689 7.48898 6.7436 7.62066 6.74475 7.75344C6.7459 7.88622 6.77349 8.01744 6.8259 8.13944C6.87831 8.26145 6.95449 8.37179 7.05 8.46404L10.586 12L7.05 15.536C6.95449 15.6283 6.87831 15.7386 6.8259 15.8606C6.77349 15.9826 6.7459 16.1139 6.74475 16.2466C6.7436 16.3794 6.7689 16.5111 6.81918 16.634C6.86946 16.7569 6.94371 16.8685 7.03761 16.9624C7.1315 17.0563 7.24315 17.1306 7.36605 17.1809C7.48894 17.2311 7.62062 17.2564 7.7534 17.2553C7.88618 17.2541 8.0174 17.2265 8.13941 17.1741C8.26141 17.1217 8.37175 17.0456 8.464 16.95L12 13.414L15.536 16.95C15.6282 17.0456 15.7386 17.1217 15.8606 17.1741C15.9826 17.2265 16.1138 17.2541 16.2466 17.2553C16.3794 17.2564 16.5111 17.2311 16.634 17.1809C16.7569 17.1306 16.8685 17.0563 16.9624 16.9624C17.0563 16.8685 17.1305 16.7569 17.1808 16.634C17.2311 16.5111 17.2564 16.3794 17.2553 16.2466C17.2541 16.1139 17.2265 15.9826 17.1741 15.8606C17.1217 15.7386 17.0455 15.6283 16.95 15.536L13.414 12L16.95 8.46404Z"
                fill="black"
              />
            </svg>
            <div className="attached-files">
              <p className="files-title">Attached File</p>
              <p className="files-subtitle">
                {selectedApplicantDocument?.name}
              </p>
              <div>
                <div className="file-item">
                  <div className="file-details">
                    <svg
                      width="29"
                      height="30"
                      viewBox="0 0 29 30"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <g clip-path="url(#clip0_13_4883)">
                        <path
                          d="M6.76672 15.9667H5.80005V14.0333H6.76672C7.02309 14.0333 7.26897 14.1352 7.45025 14.3165C7.63154 14.4977 7.73338 14.7436 7.73338 15C7.73338 15.2564 7.63154 15.5022 7.45025 15.6835C7.26897 15.8648 7.02309 15.9667 6.76672 15.9667ZM13.5334 19.8333V14.0333H14.5C14.7564 14.0333 15.0023 14.1352 15.1836 14.3165C15.3649 14.4977 15.4667 14.7436 15.4667 15V18.8667C15.4667 19.123 15.3649 19.3689 15.1836 19.5502C15.0023 19.7315 14.7564 19.8333 14.5 19.8333H13.5334Z"
                          fill="#432101"
                        />
                        <path
                          fill-rule="evenodd"
                          clip-rule="evenodd"
                          d="M1.93359 3.4C1.93359 2.63087 2.23913 1.89325 2.78298 1.34939C3.32684 0.805535 4.06447 0.5 4.83359 0.5L20.7005 0.5L27.0669 6.86647V26.6C27.0669 27.3691 26.7614 28.1068 26.2175 28.6506C25.6737 29.1945 24.9361 29.5 24.1669 29.5H4.83359C4.06447 29.5 3.32684 29.1945 2.78298 28.6506C2.23913 28.1068 1.93359 27.3691 1.93359 26.6V3.4ZM6.76693 12.1H3.86693V21.7667H5.80026V17.9H6.76693C7.53605 17.9 8.27368 17.5945 8.81754 17.0506C9.36139 16.5068 9.66693 15.7691 9.66693 15C9.66693 14.2309 9.36139 13.4932 8.81754 12.9494C8.27368 12.4055 7.53605 12.1 6.76693 12.1ZM14.5003 12.1H11.6003V21.7667H14.5003C15.2694 21.7667 16.007 21.4611 16.5509 20.9173C17.0947 20.3734 17.4003 19.6358 17.4003 18.8667V15C17.4003 14.2309 17.0947 13.4932 16.5509 12.9494C16.007 12.4055 15.2694 12.1 14.5003 12.1ZM19.3336 21.7667V12.1H25.1336V14.0333H21.2669V15.9667H23.2003V17.9H21.2669V21.7667H19.3336Z"
                          fill="#432101"
                        />
                      </g>
                      <defs>
                        <clipPath id="clip0_13_4883">
                          <rect
                            width="29"
                            height="29"
                            fill="white"
                            transform="translate(0 0.5)"
                          />
                        </clipPath>
                      </defs>
                    </svg>
                    <div className="flex">
                      <p className="file-name">
                        {selectedApplicantDocument?.name}
                      </p>
                    </div>
                  </div>
                  <div className="file-actions">
                    <button
                      onClick={() =>
                        handleDownload(
                          selectedApplicantDocument?.uuid!,
                          selectedApplicantDocument?.type!
                        )
                      }
                      className="download-btn"
                    >
                      <svg
                        width="12"
                        height="12"
                        viewBox="0 0 12 12"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                      >
                        <path
                          d="M6.00033 8.38464C5.91144 8.38464 5.8281 8.37086 5.75033 8.3433C5.67255 8.31575 5.60033 8.26842 5.53366 8.2013L3.13366 5.8013C3.00033 5.66797 2.93633 5.51242 2.94166 5.33464C2.94699 5.15686 3.01099 5.0013 3.13366 4.86797C3.26699 4.73464 3.42544 4.6653 3.60899 4.65997C3.79255 4.65464 3.95077 4.71842 4.08366 4.8513L5.33366 6.1013V1.33464C5.33366 1.14575 5.39766 0.987527 5.52566 0.859971C5.65366 0.732416 5.81188 0.668415 6.00033 0.667971C6.18877 0.667527 6.34721 0.731527 6.47566 0.859971C6.6041 0.988416 6.66788 1.14664 6.66699 1.33464V6.1013L7.91699 4.8513C8.05033 4.71797 8.20877 4.65397 8.39233 4.6593C8.57588 4.66464 8.7341 4.73419 8.86699 4.86797C8.98921 5.0013 9.05321 5.15686 9.05899 5.33464C9.06477 5.51242 9.00077 5.66797 8.86699 5.8013L6.46699 8.2013C6.40033 8.26797 6.3281 8.3153 6.25033 8.3433C6.17255 8.3713 6.08921 8.38508 6.00033 8.38464ZM2.00033 11.3346C1.63366 11.3346 1.31988 11.2042 1.05899 10.9433C0.798103 10.6824 0.667437 10.3684 0.666992 10.0013V8.66797C0.666992 8.47908 0.730992 8.32086 0.858992 8.19331C0.986992 8.06575 1.14521 8.00175 1.33366 8.0013C1.5221 8.00086 1.68055 8.06486 1.80899 8.19331C1.93744 8.32175 2.00121 8.47997 2.00033 8.66797V10.0013H10.0003V8.66797C10.0003 8.47908 10.0643 8.32086 10.1923 8.19331C10.3203 8.06575 10.4785 8.00175 10.667 8.0013C10.8554 8.00086 11.0139 8.06486 11.1423 8.19331C11.2708 8.32175 11.3345 8.47997 11.3337 8.66797V10.0013C11.3337 10.368 11.2032 10.682 10.9423 10.9433C10.6814 11.2046 10.3674 11.3351 10.0003 11.3346H2.00033Z"
                          fill="black"
                        />
                      </svg>

                      <p>Download</p>
                    </button>
                    <button
                      onClick={() =>
                        viewFile
                          ? setViewFile("")
                          : handleView(
                              selectedApplicantDocument?.uuid!,
                              selectedApplicantDocument?.type!
                            )
                      }
                      className="view-btn"
                    >
                      <p>{viewFile ? "Close" : "View"}</p>
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </Popup>
  );
}
